import { encodeRowForSqlite, OFFLINE_STORES } from './offlineStorageAdapter.js';

const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
const sha = /^[a-f0-9]{64}$/;
const positive = value => Number.isSafeInteger(value) && value > 0;
const extensions = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif',
    'image/bmp': 'bmp', 'image/avif': 'avif', 'application/pdf': 'pdf' };
const hash = async text => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)))].map(byte => byte.toString(16).padStart(2, '0')).join('');
const copy = value => JSON.parse(JSON.stringify(value));
const fail = () => { throw Error('La intención de subida requiere revisión. Se conservan el archivo y sus datos.'); };
function text(value, max, fallback = '') {
    const result = value ?? fallback;
    if (result === null && fallback === null) return null;
    if (typeof result !== 'string' || result.length > max) fail();
    return result;
}
function number(value, min, max) {
    if (value == null) return null;
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) fail();
    return value;
}

export function attachmentQueueSnapshot(entry) {
    return JSON.stringify(encodeRowForSqlite(OFFLINE_STORES.MEDIA, entry));
}

// El manifiesto literal se conserva, incluyendo ceros y null. Reintentar no consulta ni rebasa una versión nueva.
export async function createOfflineAttachmentCommand({ file, entry, empresaId, kind = 'image', expectedVersion = null, formRevision = null }) {
    if (!file || !entry || !uuid.test(file.id) || !positive(file.userId) || !positive(file.visitaId) || !positive(empresaId)
        || file.id !== entry.id || file.userId !== entry.userId || file.visitaId !== entry.visitaId
        || file.empresaId != null && file.empresaId !== empresaId
        || file.path !== entry.filePath || file.size !== entry.size || !sha.test(file.sha256)
        || file.kind !== kind || !['image', 'document'].includes(kind) || !Number.isSafeInteger(file.size) || file.size < 1) fail();
    const mime = text(entry.mimeType, 100), filename = text(entry.filename, 255);
    if (!filename || !extensions[mime] || /[\x00-\x1f/\\]/.test(filename)
        || kind === 'image' && (mime === 'application/pdf' || file.size > 5 * 1024 * 1024)
        || kind === 'document' && (!['application/pdf', 'image/jpeg', 'image/png'].includes(mime) || file.size > 20 * 1024 * 1024)) fail();
    const metadata = entry.metadata || {};
    const instanceRef = kind === 'image' ? text(metadata.instanceRef, 32) : '';
    if (instanceRef && (!/^\d+_\d+$/.test(instanceRef) || !Number.isSafeInteger(expectedVersion) || expectedVersion < 0 || !sha.test(formRevision || ''))) fail();
    if (kind === 'document' && Object.entries(metadata).some(([key,value]) => key !== 'acceptanceRole' && value !== null && value !== undefined && value !== '')) fail();
    if (metadata.acceptanceRole !== undefined && (kind !== 'document' || !['evidence','reference'].includes(metadata.acceptanceRole))) fail();
    const manifest = JSON.stringify({ contract: 2, kind, client_uuid: file.id, user_id: file.userId, empresa_id: empresaId, visita_id: file.visitaId,
        file_sha256: file.sha256, file_size: file.size, filename, mime_type: mime, descripcion: text(entry.descripcion, 255),
        instance_ref: instanceRef || null, instance_label: kind === 'image' ? text(metadata.instanceLabel, 180) : null,
        expected_version: instanceRef ? expectedVersion : null, form_revision: instanceRef ? formRevision : null,
        gps_lat: kind === 'image' ? number(metadata.gpsLat, -90, 90) : null,
        gps_lng: kind === 'image' ? number(metadata.gpsLng, -180, 180) : null,
        gps_accuracy: kind === 'image' ? number(metadata.gpsAccuracy, 0, Number.MAX_VALUE) : null,
        gps_captured_at: kind === 'image' ? text(metadata.gpsCapturedAt, 40, null) : null,
        ...(metadata.acceptanceRole ? {acceptance_role: metadata.acceptanceRole} : {}) });
    return { schemaVersion: 1, idempotencyKey: `attachment:${file.id}`, manifest, manifestSha256: await hash(manifest), queueSnapshot: copy(entry) };
}

export async function validateOfflineAttachmentCommand(file, command) {
    if (command?.schemaVersion !== 1 || typeof command.manifest !== 'string' || !sha.test(command.manifestSha256 || '')) fail();
    let manifest;
    try { manifest = JSON.parse(command.manifest); } catch { fail(); }
    if (!manifest || typeof manifest !== 'object') fail();
    const expected = await createOfflineAttachmentCommand({ file, entry: command.queueSnapshot, empresaId: manifest.empresa_id,
        kind: manifest.kind, expectedVersion: manifest.expected_version, formRevision: manifest.form_revision });
    if (expected.manifest !== command.manifest || expected.manifestSha256 !== command.manifestSha256 || expected.idempotencyKey !== command.idempotencyKey) fail();
    return manifest;
}

// Solo HTTP 200 con efecto, identidad, contenido y manifiesto exactos es ACK. Un 202 o success aislado conserva los datos.
export async function verifyOfflineAttachmentReceipt(file, command, response) {
    const manifest = await validateOfflineAttachmentCommand(file, command), data = response?.data;
    const filename = `visitas/${file.visitaId}/offline/${file.userId}/${file.id}-${file.sha256}.${extensions[manifest.mime_type]}`;
    if (response?.status !== 200 || data?.success !== true || data.upload_contract !== 2
        || data.effect !== (manifest.kind === 'image' ? 'image_added' : 'document_added') || data.applied_operation_uuid !== file.id
        || data.user_id !== file.userId || data.empresa_id !== manifest.empresa_id || data.visita_id !== file.visitaId
        || data.file_sha256 !== file.sha256 || data.file_size !== file.size || data.manifest_sha256 !== command.manifestSha256
        || data.filename !== filename || typeof data.url !== 'string' || !data.url.trim()) fail();
    // No persistir cabeceras, tokens ni campos ajenos al comprobante mínimo.
    return { status: 200, data: Object.fromEntries(['success', 'upload_contract', 'effect', 'applied_operation_uuid', 'user_id', 'empresa_id', 'visita_id',
        'file_sha256', 'file_size', 'manifest_sha256', 'filename', 'url'].map(key => [key, data[key]])) };
}
